import React, { useEffect, useState } from "react";
import { fetchUsers, addUser, updateUser, deleteUser } from "../api";
import Sidebar from "./Sidebar";
import TopBar from "../components/TopBar";

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const [resetUser, setResetUser] = useState(null);
    const [newPassword, setNewPassword] = useState("");

    const [userData, setUserData] = useState({
        name: "",
        email: "",
        role: "user"
    });
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const userRole = localStorage.getItem("role");

    // Pagination & search
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const itemsPerPage = 10;

    useEffect(() => {
        getUsers();
    }, []);

    const getUsers = async () => {
        try {
            const response = await fetchUsers();
            const filteredUsers = response.data.filter(user => user.role !== "superadmin");
            setUsers(filteredUsers);
            setLoading(false);
        } catch (err) {
            setError("Failed to load users");
            setLoading(false);
        }
    };

    // Filtered and paginated users
    const filteredUsers = users.filter(
        u =>
            u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            u.role.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;
        try {
            await deleteUser(id);
            setUsers(users.filter(user => user._id !== id));
        } catch (err) {
            alert("Error deleting user");
        }
    };

    const handleOpenModal = (user = null) => {
        setEditingUser(user);
        if (user) {
            setUserData({
                name: user.name,
                email: user.email,
                role: user.role,
                password: ""
            });
        } else {
            setUserData({
                name: "",
                email: "",
                role: "user",
                password: ""
            });
        }
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingUser(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingUser) {
                await updateUser(editingUser._id, userData);
            } else {
                await addUser(userData);
            }
            getUsers();
            handleCloseModal();
        } catch (err) {
            alert(err?.response?.data?.message || "Error saving user");
        }
    };

    const handleOpenReset = (user) => {
        setResetUser(user);
        setNewPassword("");
        setResetModalOpen(true);
    };

    const handleResetPassword = async () => {
        try {
            await updateUser(resetUser._id, { password: newPassword });
            alert("Password updated successfully");
            setResetModalOpen(false);
        } catch (err) {
            alert("Failed to reset password");
        }
    };

    return (
        <div style={{ display: "flex", height: "100vh" }}>
            <Sidebar setIsSidebarOpen={setIsSidebarOpen} />
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    transition: "margin-left 0.3s ease",
                    marginLeft: isSidebarOpen ? "250px" : "80px",
                    padding: "20px",
                }}
            >
                <TopBar />

                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-10 mx-auto p-4">

                            {/* Header: Add User + Search */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <button
                                    onClick={() => handleOpenModal()}
                                    className="btn btn-primary"
                                >
                                    + Add New User
                                </button>

                                <div style={{ width: "200px" }}>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Search..."
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                            </div>

                            {loading ? (
                                <p>Loading...</p>
                            ) : error ? (
                                <p className="text-danger">{error}</p>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover">
                                            <thead className="table-dark">
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Email</th>
                                                    <th>Role</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedUsers.map(user => (
                                                    <tr key={user._id}>
                                                        <td>{user.name}</td>
                                                        <td>{user.email}</td>
                                                        <td>{user.role}</td>
                                                        <td>
                                                            {!(userRole === "admin" && user.role === "superadmin") && (
                                                                <button
                                                                    onClick={() => handleOpenModal(user)}
                                                                    className="btn btn-primary btn-sm mx-2"
                                                                >
                                                                    Edit
                                                                </button>
                                                            )}
                                                            {userRole === "superadmin" && (
                                                                <>
                                                                    <button
                                                                        onClick={() => handleDelete(user._id)}
                                                                        className="btn btn-danger btn-sm"
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleOpenReset(user)}
                                                                        className="btn btn-warning btn-sm mx-2"
                                                                    >
                                                                        Reset Password
                                                                    </button>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <nav>
                                            <ul className="pagination justify-content-center">
                                                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                    <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>Previous</button>
                                                </li>
                                                {Array.from({ length: totalPages }, (_, i) => (
                                                    <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                                                        <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                                    </li>
                                                ))}
                                                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                    <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
                                                </li>
                                            </ul>
                                        </nav>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Modal for Add/Edit User */}
                    {modalOpen && (
                        <div className="modal fade show d-block" tabIndex="-1">
                            <div className="modal-dialog">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">
                                            {editingUser ? "Edit User" : "Add New User"}
                                        </h5>
                                        <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                                    </div>

                                    <div className="modal-body">
                                        <form onSubmit={handleSubmit}>
                                            <div className="mb-3">
                                                <label className="form-label">Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={userData.name}
                                                    onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                                                    required
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label">Email</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    value={userData.email}
                                                    onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                                                    required
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label">Role</label>
                                                <select
                                                    className="form-control"
                                                    value={userData.role}
                                                    onChange={(e) => setUserData({ ...userData, role: e.target.value })}
                                                >
                                                    <option value="user">User</option>
                                                    <option value="admin">Admin</option>
                                                </select>

                                            </div>

                                            {/* {!editingUser && (
                                                <div className="mb-3">
                                                    <label className="form-label">Password</label>
                                                    <input
                                                        type="password"
                                                        className="form-control"
                                                        value={userData.password}
                                                        onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                                                        required
                                                    />
                                                </div>
                                            )} */}

                                            <button type="submit" className="btn btn-primary">
                                                {editingUser ? "Update User" : "Add User"}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Modal for Reset Password */}
                    {resetModalOpen && (
                        <div className="modal fade show d-block" tabIndex="-1">
                            <div className="modal-dialog">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">
                                            Reset Password for {resetUser?.name}
                                        </h5>
                                        <button className="btn-close" onClick={() => setResetModalOpen(false)}></button>
                                    </div>

                                    <div className="modal-body">
                                        <input
                                            type="password"
                                            className="form-control"
                                            placeholder="Enter new password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                        />
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => setResetModalOpen(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="btn btn-success"
                                            onClick={handleResetPassword}
                                            disabled={!newPassword}
                                        >
                                            Update Password
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default AdminUsers;
